'use client';
import React, { useEffect, useMemo, useState } from 'react';

import {
  Hydrate,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { FlagsProvider } from 'flagged';
import ConfigProvider from 'antd/es/config-provider';
import tr_TR from 'antd/lib/locale/tr_TR';
import { ThemeProvider } from 'styled-components';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { usePathname, useRouter } from 'next/navigation';

import theme from '@theme';
import GlobalStyles from '@utils/theme/global/GlobalStyles';
import { GTMPageView } from '@utils/gtm/pageView';
import { GTMScrollDepth } from '@utils/gtm/scroll-gtm';
import { RouterProvider } from '@contexts/router';
import AuthProvider, { LoginInfo } from '@contexts/auth/AuthContext';
import AuthTokenExpiration from '@utils/auth/AuthTokenExpiration';
import { SearchBarProvider } from '@contexts/searchbar';
import { IsVisibleFooterProvider } from '@contexts/footer';
import { DeviceProvider } from '@contexts/breakpoints';

import FEATURE_FLAGS from '../feature-flags.json';

const ClientWrapper = ({
  children,
  loginInfo,
  deviceInfo,
}: {
  children: React.ReactNode;
  loginInfo: LoginInfo;
  deviceInfo: { xl: boolean; xs: boolean; md: boolean };
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            refetchOnWindowFocus: false,
            staleTime: 0,
            cacheTime: 0,
          },
        },
      })
  );

  // added for prefetch set false problem doesn't work on production
  // https://github.com/vercel/next.js/discussions/24437#discussioncomment-3679709
  useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    router.prefetch = async () => {};
  }, [router]);

  /**
   * Note: when clicking a Next Link, user will not scroll to the top of the page.
   * This is a workaround to fix that issue.
   */
  useEffect(() => {
    window?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);

  return (
    <FlagsProvider features={FEATURE_FLAGS}>
      <ConfigProvider
        locale={tr_TR}
        prefixCls='setur'
        theme={{
          token: {
            colorPrimary: theme.palette.primaryBrand,
            fontFamily: theme.fonts.main,
            borderRadius: 2,
            fontSize: 16,
          },
        }}
      >
        <ThemeProvider theme={theme}>
          <GoogleReCaptchaProvider
            reCaptchaKey={process.env.NEXT_PUBLIC_SITE_KEY ?? ''}
            scriptProps={{
              async: true,
              defer: true,
              appendTo: 'head',
            }}
          >
            <GlobalStyles />
            <GTMPageView />
            <GTMScrollDepth />

            <QueryClientProvider client={queryClient}>
              <Hydrate state={undefined}>
                <RouterProvider>
                  <AuthProvider loginInfo={loginInfo}>
                    <DeviceProvider params={deviceInfo}>
                      <AuthTokenExpiration />
                      <SearchBarProvider>
                        <IsVisibleFooterProvider>
                          {children}
                        </IsVisibleFooterProvider>
                      </SearchBarProvider>
                    </DeviceProvider>
                  </AuthProvider>
                </RouterProvider>
              </Hydrate>
              <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
          </GoogleReCaptchaProvider>
        </ThemeProvider>
      </ConfigProvider>
    </FlagsProvider>
  );
};

export default ClientWrapper;
